import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { authApi } from '../services/api';
import { createLocalAudio, SPOKEN_TEXT_ATTRS } from '../lib/speechPlayback';
import {
  CLIENT_KEY_STORAGE,
  MENTEE_ENTRY_GATE_STORAGE,
  MENTEE_KEY_STORAGE,
  REGISTRATION_PATHWAY_STORAGE,
  SCREENS,
  resolveGrandFoyerRoute,
} from '../lib/screenRegistry';
import { ROUTES } from '../lib/classroomRoutes';
import { isMaintenancePreviewEmbed } from '../lib/masterAdminAccess';
import { isMasterTestingSessionActive } from '../lib/masterTestingKey';
import './Introduction.css';

const ASSETS = {
  audio: encodeURI('/Assets/Founder/welcome_mentor_classroom.wav'),
  script: encodeURI('/Assets/Founder/welcome_classroom.txt'),
  logo: encodeURI('/Assets/logo/mentology- logo.png'),
};

const EXECUTIVE_INTRO_SCRIPT =
  'Welcome to the Mentology mentor learning journey.  Welcome, mentors.  You are embarking on a journey to refine your mentoring craft using the Mentology framework.  This program is designed not just to teach you how to mentor, but to help you understand the deep psychological and developmental stages of the mentoring relationship.  We believe in a continuous learning mindset.  This space is your laboratory to reflect on your expertise, identify your growth edges, and bridge the gap between thinking and doing.  Your first steps: head over to the classwork tab and start at module 10: Welcome and Orientation.  This will introduce you to the five stages of the mentoring journey and your study partner.  Complete the mentoring commitment.  This is our shared code of ethics.  The roadmap: once you reach module 03, you will complete your self-assessment to generate your personalized learning roadmap.  We are excited to have you in the Mentology community.  Let\'s begin.  Click on classwork tab.';

function normalizeFounderScript(raw: string) {
  const trimmed = raw.trim();
  const anchor = 'Welcome to the Mentology mentor learning journey';
  const anchorIndex = trimmed.indexOf(anchor);
  if (anchorIndex >= 0) {
    return trimmed.slice(anchorIndex).trim();
  }
  return trimmed;
}

const WPM = 45;
const MS_PER_CHAR = ((60 / WPM) * 1000) / 5;

export default function Introduction() {
  const { user, refresh } = useAuth();
  const navigate = useNavigate();
  const [scriptText, setScriptText] = useState(EXECUTIVE_INTRO_SCRIPT);
  const [displayText, setDisplayText] = useState('');
  const [isPlaybackActive, setIsPlaybackActive] = useState(false);
  const [showProceed, setShowProceed] = useState(false);
  const [proceeding, setProceeding] = useState(false);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const charIndex = useRef(0);
  const fallbackTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const loadTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const rafRef = useRef<number | null>(null);
  const playbackStartedRef = useRef(false);
  const proceedRef = useRef(false);

  const stopFounderAudio = useCallback(() => {
    if (rafRef.current !== null) {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    }
    if (fallbackTimer.current) {
      clearTimeout(fallbackTimer.current);
      fallbackTimer.current = null;
    }
    if (loadTimeoutRef.current) {
      clearTimeout(loadTimeoutRef.current);
      loadTimeoutRef.current = null;
    }

    const audio = audioRef.current;
    if (!audio) return;
    audio.pause();
    audio.currentTime = 0;
    audio.src = '';
    audioRef.current = null;
    setIsPlaybackActive(false);
  }, []);

  useEffect(() => {
    let cancelled = false;

    fetch(ASSETS.script)
      .then((res) => (res.ok ? res.text() : Promise.reject()))
      .then((text) => {
        if (cancelled) return;
        const normalized = normalizeFounderScript(text);
        if (normalized) setScriptText(normalized);
      })
      .catch(() => {
        if (!cancelled) setScriptText(EXECUTIVE_INTRO_SCRIPT);
      });

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (isMaintenancePreviewEmbed()) {
      return () => {
        stopFounderAudio();
      };
    }

    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = '';
      stopFounderAudio();
    };
  }, [stopFounderAudio]);

  useEffect(() => {
    playbackStartedRef.current = false;
    charIndex.current = 0;
    setDisplayText('');
    setShowProceed(false);
    setIsPlaybackActive(false);
    stopFounderAudio();

    const script = scriptText;
    const scriptLength = script.length;
    if (!scriptLength) return undefined;

    const revealProceedGate = () => {
      setDisplayText(script);
      setShowProceed(true);
      setIsPlaybackActive(false);
      if (rafRef.current !== null) {
        cancelAnimationFrame(rafRef.current);
        rafRef.current = null;
      }
    };

    const syncTypewriterFallback = () => {
      if (charIndex.current < scriptLength) {
        charIndex.current += 1;
        setDisplayText(script.slice(0, charIndex.current));
        fallbackTimer.current = setTimeout(syncTypewriterFallback, MS_PER_CHAR);
      } else {
        revealProceedGate();
      }
    };

    const clearLoadTimeout = () => {
      if (loadTimeoutRef.current) {
        clearTimeout(loadTimeoutRef.current);
        loadTimeoutRef.current = null;
      }
    };

    const startFallbackTypewriter = () => {
      if (playbackStartedRef.current) return;
      playbackStartedRef.current = true;
      clearLoadTimeout();
      setIsPlaybackActive(true);
      charIndex.current = 0;
      setDisplayText('');
      syncTypewriterFallback();
    };

    const syncTypewriterToAudio = () => {
      const audio = audioRef.current;
      if (!audio) return;

      if (audio.paused || audio.ended) {
        rafRef.current = requestAnimationFrame(syncTypewriterToAudio);
        return;
      }

      if (audio.duration && Number.isFinite(audio.duration) && audio.duration > 0) {
        const progress = Math.min(audio.currentTime / audio.duration, 1);
        const targetIndex = Math.min(scriptLength, Math.max(0, Math.ceil(progress * scriptLength)));
        charIndex.current = targetIndex;
        setDisplayText(script.slice(0, targetIndex));

        if (progress >= 1 || targetIndex >= scriptLength) {
          revealProceedGate();
          return;
        }
      }

      rafRef.current = requestAnimationFrame(syncTypewriterToAudio);
    };

    const startAudioSynchronizedTypewriter = () => {
      clearLoadTimeout();
      if (fallbackTimer.current) {
        clearTimeout(fallbackTimer.current);
        fallbackTimer.current = null;
      }
      if (rafRef.current !== null) {
        cancelAnimationFrame(rafRef.current);
        rafRef.current = null;
      }
      playbackStartedRef.current = true;
      setIsPlaybackActive(true);
      charIndex.current = 0;
      setDisplayText('');
      rafRef.current = requestAnimationFrame(syncTypewriterToAudio);
    };

    const audio = createLocalAudio(ASSETS.audio);
    audioRef.current = audio;

    const onPlay = () => {
      startAudioSynchronizedTypewriter();
    };

    const onEnded = () => {
      revealProceedGate();
    };

    const onError = () => {
      startFallbackTypewriter();
    };

    audio.addEventListener('play', onPlay);
    audio.addEventListener('ended', onEnded);
    audio.addEventListener('error', onError);

    const attemptPlayback = () => {
      audio.currentTime = 0;
      audio.play().catch(() => {
        startFallbackTypewriter();
      });
    };

    if (audio.readyState >= HTMLMediaElement.HAVE_METADATA) {
      attemptPlayback();
    } else {
      audio.addEventListener('loadedmetadata', attemptPlayback, { once: true });
      audio.addEventListener('canplay', attemptPlayback, { once: true });
    }

    return () => {
      audio.removeEventListener('play', onPlay);
      audio.removeEventListener('ended', onEnded);
      audio.removeEventListener('error', onError);
      stopFounderAudio();
    };
  }, [scriptText, stopFounderAudio]);

  const getProceedConfig = useCallback((hasBio = false) => {
    const registrationPathway = sessionStorage.getItem(REGISTRATION_PATHWAY_STORAGE);
    const storedKey =
      sessionStorage.getItem(MENTEE_KEY_STORAGE)
      || sessionStorage.getItem(CLIENT_KEY_STORAGE)
      || user?.userToken
      || '';

    const foyerRoute = resolveGrandFoyerRoute(storedKey, registrationPathway, hasBio);
    if (foyerRoute) return foyerRoute;

    if (!user) return { label: '[ Continue ]', route: ROUTES.CLASSROOM_PORTAL };
    switch (user.role) {
      case 'PRO':
      case 'ELT':
        return hasBio
          ? { label: 'Enter Learning Space', route: ROUTES.LEARNING_ROOM }
          : { label: 'Continue to Individual Setup', route: ROUTES.STUDENT_SETUP };
      case 'ORG':
        return { label: '[ Enter Admin Console ]', route: ROUTES.SYSTEM_ADMIN };
      default:
        return { label: '[ Continue ]', route: ROUTES.LEARNING_ROOM };
    }
  }, [user]);

  useEffect(() => {
    if (isMaintenancePreviewEmbed()) return;
    if (!user || user.accountState === 'INITIALIZING') return;
    if (sessionStorage.getItem(MENTEE_ENTRY_GATE_STORAGE) === 'portal') return;
    const { route } = getProceedConfig();
    navigate(route, { replace: true });
  }, [user, navigate, getProceedConfig]);

  const handleProceed = async () => {
    if (proceeding || proceedRef.current) return;
    proceedRef.current = true;
    setProceeding(true);
    stopFounderAudio();
    try {
      if (user?.accountState === 'INITIALIZING') {
        await authApi.completeHandover();
      }
      await authApi.introComplete();
      sessionStorage.removeItem(MENTEE_ENTRY_GATE_STORAGE);
      await refresh();
      const session = await authApi.me();
      const { route } = getProceedConfig(Boolean(session.bio));
      navigate(route, { replace: true });
    } catch (error) {
      console.error('[Introduction] Proceed failed', error);
      const authStatus = (error as { status?: number })?.status;
      const fallbackRoute = getProceedConfig().route;
      if (authStatus === 401 || authStatus === 500) {
        if (user || isMasterTestingSessionActive()) {
          sessionStorage.removeItem(MENTEE_ENTRY_GATE_STORAGE);
          navigate(fallbackRoute, { replace: true });
          return;
        }
      }
      proceedRef.current = false;
    } finally {
      setProceeding(false);
    }
  };

  const proceedConfig = getProceedConfig();

  return (
    <div className="introduction-cinematic" data-screen-id={SCREENS.MPL07}>
      <div className="introduction-stage">
        <div className="introduction-brand-stack">
          <img
            src={ASSETS.logo}
            alt=""
            aria-hidden="true"
            className="introduction-logo brand-logo"
            onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
          />

          <div className={`introduction-text-box ${SPOKEN_TEXT_ATTRS.className}`}>
            <div
              className={`introduction-typewriter-body scannable-bullet-text ${SPOKEN_TEXT_ATTRS.className}`}
              aria-live="polite"
              data-playback-active={isPlaybackActive ? 'true' : 'false'}
            >
              {displayText}
              {isPlaybackActive && !showProceed && displayText.length < scriptText.length && (
                <span className="introduction-cursor">|</span>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className={`introduction-gate ${showProceed ? 'introduction-gate-visible' : ''}`}>
        <button
          type="button"
          className="btn-primary introduction-proceed-btn"
          onClick={handleProceed}
          disabled={proceeding}
          aria-hidden={!showProceed}
          tabIndex={showProceed ? 0 : -1}
        >
          {proceeding ? 'Advancing...' : proceedConfig.label}
        </button>
      </div>
    </div>
  );
}
